import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Stage, ChapterTag } from "@/components/Reveal";
import { Arch, Traveler, Thread } from "@/components/Motifs";

const QUESTION = "What are you in the middle of becoming, even if you do not have language for it yet?";
const ENCOUNTER_RANGES = {
  onePrompt: [0.68, 0.715, 0.745, 0.775],
  twoPeople: [0.775, 0.805, 0.83, 0.855],
  conversation: [0.855, 0.885, 0.905, 0.925],
  closing: [0.93, 0.975],
};

const ChamberArchitecture = ({ glow, archAsm, a1, a2, a3, a4, a5 }) => (
  <>
    <motion.div className="th-chambglow" style={{ opacity: glow }} />
    <motion.div className="absolute inset-0 pointer-events-none" style={{ opacity: glow }}>
      <div className="th-beam" style={{ left: "30%" }} aria-hidden="true" />
      <div className="th-beam th-beam-blue" style={{ right: "26%", animationDelay: "-6s" }} aria-hidden="true" />
    </motion.div>
    <motion.div className="absolute inset-0 pointer-events-none" style={{ scale: archAsm, originX: 0.5, originY: 1 }}>
      <motion.div className="absolute left-1/2 -translate-x-1/2 bottom-[4%]" style={{ opacity: a5 }}>
        <Arch style={{ height: "118vh" }} sw={1} stroke="var(--muted)" />
      </motion.div>
      <motion.div className="absolute left-1/2 -translate-x-1/2 bottom-[4%]" style={{ opacity: a4 }}>
        <Arch style={{ height: "104vh" }} sw={1} stroke="var(--muted)" />
      </motion.div>
      <motion.div className="absolute left-1/2 -translate-x-1/2 bottom-[4%]" style={{ opacity: a1 }}>
        <Arch style={{ height: "90vh" }} sw={1} stroke="var(--muted)" />
      </motion.div>
      <motion.div className="absolute left-1/2 -translate-x-1/2 bottom-[4%]" style={{ opacity: a2 }}>
        <Arch style={{ height: "70vh" }} sw={1} stroke="var(--ink-soft)" />
      </motion.div>
      <motion.div className="absolute left-1/2 -translate-x-1/2 bottom-[4%]" style={{ opacity: a3 }}>
        <Arch style={{ height: "52vh" }} sw={1.2} stroke="var(--ink-soft)" />
      </motion.div>
    </motion.div>
  </>
);

const EncounterConnection = ({ t1, t2, t3, t13Op, ax, bx, bOp, midPulse }) => (
  <>
    <Thread d="M 330 330 C 430 210, 570 210, 670 330" viewBox="0 0 1000 500" className="absolute inset-0 w-full h-full" progress={t1} opacity={t13Op} stroke="var(--sage-deep)" sw={1.1} />
    <Thread d="M 330 340 C 440 430, 560 430, 670 340" viewBox="0 0 1000 500" className="absolute inset-0 w-full h-full" progress={t2} stroke="var(--amber)" sw={1.1} />
    <Thread d="M 330 332 C 450 296, 550 296, 670 332" viewBox="0 0 1000 500" className="absolute inset-0 w-full h-full" progress={t3} opacity={t13Op} stroke="var(--muted)" sw={1} />
    <motion.div className="absolute bottom-[22%] left-1/2" style={{ x: ax }} data-testid="encounter-traveler-a">
      <Traveler className="h-14 md:h-16 -translate-x-1/2" />
    </motion.div>
    <motion.div className="absolute bottom-[22%] left-1/2" style={{ x: bx, opacity: bOp }} data-testid="encounter-traveler-b">
      <Traveler className="h-14 md:h-16 -translate-x-1/2" tone="var(--ink-soft)" />
    </motion.div>
    <motion.span className="th-pulse absolute bottom-[27%]" style={{ opacity: midPulse, width: 8, height: 8, left: "calc(50% - 4px)" }} />
  </>
);

const EncounterPrompt = ({ promptIn, promptInY, promptX, promptOp, promptScale, planeOp, planeY, planeDim }) => (
  <>
    <motion.div className="absolute inset-0 flex items-center justify-center px-[6vw]" style={{ opacity: promptIn, y: promptInY }}>
      <motion.div className="max-w-[44rem] text-center" style={{ x: promptX, opacity: promptOp, scale: promptScale }}>
        <p className="th-mini-label" style={{ marginBottom: 18 }}>YOUR OPENING · DEEP QUESTION</p>
        <h3 className="th-prompt" data-testid="encounter-prompt">{QUESTION}</h3>
      </motion.div>
    </motion.div>
    <motion.div className="th-ui-plane" style={{ opacity: planeOp, y: planeY }} data-testid="encounter-ui-plane">
      <motion.div className="th-plane-frame th-plane-tall" style={{ opacity: planeDim }}>
        <img src="/assets/img3299.webp" alt="Threshold — the deep question as it appears in the app" loading="lazy" decoding="async" />
      </motion.div>
    </motion.div>
  </>
);

const useEncounterMotion = (progress) => ({
  ax: useTransform(progress, [0.02, 0.28], ["-30vw", "-7vw"]),
  bx: useTransform(progress, [0.02, 0.28, 0.93, 0.99], ["30vw", "7vw", "7vw", "16vw"]),
  bOp: useTransform(progress, [0.05, 0.24, 0.93, 0.99], [0, 1, 1, 0]),
  t13Op: useTransform(progress, [0.9, 0.96], [1, 0]),
  a1: useTransform(progress, [0.04, 0.16], [0, 0.4]),
  a2: useTransform(progress, [0.07, 0.2], [0, 0.38]),
  a3: useTransform(progress, [0.1, 0.24], [0, 0.5]),
  a4: useTransform(progress, [0.08, 0.22], [0, 0.28]),
  a5: useTransform(progress, [0.06, 0.18], [0, 0.2]),
  archAsm: useTransform(progress, [0.02, 0.26], [1.45, 1]),
  glow: useTransform(progress, [0.18, 0.32], [0, 0.5]),
  bigX: useTransform(progress, [0.1, 0.46], ["42vw", "-175vw"]),
  bigOp: useTransform(progress, [0.1, 0.15, 0.42, 0.5], [0, 1, 1, 0]),
  ghostX: useTransform(progress, [0.1, 0.46], ["30vw", "-140vw"]),
  ghostOp: useTransform(progress, [0.12, 0.2, 0.4, 0.48], [0, 0.16, 0.16, 0]),
  t1: useTransform(progress, [0.32, 0.58], [0, 1]),
  t2: useTransform(progress, [0.36, 0.64], [0, 1]),
  t3: useTransform(progress, [0.42, 0.72], [0, 1]),
  midPulse: useTransform(progress, [0.4, 0.5], [0, 1]),
  promptIn: useTransform(progress, [0.5, 0.58], [0, 1]),
  promptInY: useTransform(progress, [0.5, 0.58], [40, 0]),
  promptX: useTransform(progress, [0.64, 0.73], ["0vw", "-13vw"]),
  promptOp: useTransform(progress, [0.68, 0.76], [1, 0.16]),
  promptScale: useTransform(progress, [0.64, 0.73], [1, 0.94]),
  planeOp: useTransform(progress, [0.62, 0.72], [0, 1]),
  planeY: useTransform(progress, [0.62, 0.74], [60, 0]),
  planeDim: useTransform(progress, [0.9, 1], [1, 0.4]),
});

export default function Encounter() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const animation = useEncounterMotion(p);

  return (
    <section id="scene-encounter" ref={ref} className="th-scene" style={{ height: "340vh" }} data-testid="scene-encounter">
      <div className="th-sticky">
        <ChapterTag index="05" name="Encounter" />
        <ChamberArchitecture glow={animation.glow} archAsm={animation.archAsm} a1={animation.a1} a2={animation.a2} a3={animation.a3} a4={animation.a4} a5={animation.a5} />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div className="th-megaline th-megaline-ghost" style={{ x: animation.ghostX, opacity: animation.ghostOp }} aria-hidden="true">
            {QUESTION}
          </motion.div>
          <motion.div className="th-megaline" style={{ x: animation.bigX, opacity: animation.bigOp }} data-testid="encounter-megaline">
            {QUESTION}
          </motion.div>
        </div>
        <EncounterConnection t1={animation.t1} t2={animation.t2} t3={animation.t3} t13Op={animation.t13Op} ax={animation.ax} bx={animation.bx} bOp={animation.bOp} midPulse={animation.midPulse} />
        <EncounterPrompt promptIn={animation.promptIn} promptInY={animation.promptInY} promptX={animation.promptX} promptOp={animation.promptOp} promptScale={animation.promptScale} planeOp={animation.planeOp} planeY={animation.planeY} planeDim={animation.planeDim} />
        <Stage progress={p} range={ENCOUNTER_RANGES.onePrompt} className="th-stage-low">
          <h3 className="th-big-line th-halo">One prompt.</h3>
        </Stage>
        <Stage progress={p} range={ENCOUNTER_RANGES.twoPeople} className="th-stage-low">
          <h3 className="th-big-line th-halo">Two people.</h3>
        </Stage>
        <Stage progress={p} range={ENCOUNTER_RANGES.conversation} className="th-stage-low">
          <h3 className="th-big-line th-halo">One real conversation.</h3>
        </Stage>
        <Stage progress={p} range={ENCOUNTER_RANGES.closing} hold>
          <p className="th-closing th-halo" data-testid="encounter-closing">
            You do not need to impress them.
            <br />
            You only need to meet them.
          </p>
        </Stage>
      </div>
    </section>
  );
}
